import { MouseEvent, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import isArray from 'lodash/isArray'
import isEqual from 'lodash/isEqual'
import { v4 as uuid } from 'uuid'
import { GridCellParams, MuiEvent } from '@microservices/wiskey-react-table-component'
import { GridApiPro } from '@microservices/wiskey-react-table-component/dist/DataGrid/DataGridV7/models/gridApiPro'

import { OpenFormProps } from '@pages/ConfiguredEntity/types'

import { useExecuteCommandMutation, useFetchObjectByCodeNoCacheMutation } from '@redux/api'
import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'
import { openFormDialog } from '@redux/reducers/dialogWindowManager.reducer'
import { addFormHistory } from '@redux/reducers/formHistory.reducer'
import { setTimelineVariables } from '@redux/reducers/timeLine.reducer'

import { useAppDispatch, useExecuteActions, usePkField } from '@hooks'
import {
  checkMaxOpenedWindowsCount,
  executeCode,
  getInitialPositionWindow,
  transformCommonParametersToValues,
} from '@helpers'
import { ACTION_CODE, COMMON_PARAMETERS, EVENT_CODE, TIME_LINE_MODE } from '@constants'
import {
  ActionRow,
  ApiRestrictionData,
  DateIntervalKey,
  DIALOG_WINDOW_TYPE,
  GETVariableDTO,
  ObjectDataRecord,
  ObjectFieldDTO,
  POSTObjectDataFilter,
  SearchAssistantType,
  TimelineCellContext,
  TimelineCellMove,
  TimeLineDTOType,
  TimeLineGridDataRecord,
} from '@types'

import {
  applyTimelineCellMovesToRows,
  getCellId,
  getDateIntervalsDefaultValue,
  splitIntervalByPeriod,
} from '../helpers'

import { useCellItemDragAndDrop } from './useCellItemDragAndDrop'
import { useCellSelection } from './useCellSelection'
import { useGetObjectXActions } from './useGetObjectXActions'
import { useTimeLineRestrictions } from './useTimeLineRestrictions'

type UseHandlersParams = {
  config: TimeLineDTOType
  apiRef?: MutableRefObject<GridApiPro> | null
  searchAssistantConfig?: SearchAssistantType
  objectXInitialRestrictions: ApiRestrictionData
  objectYInitialRestrictions: ApiRestrictionData
}

export const useHandlers = (params: UseHandlersParams) => {
  const {
    config,
    apiRef,
    searchAssistantConfig,
    objectXInitialRestrictions,
    objectYInitialRestrictions,
  } = params

  const dispatch = useAppDispatch()

  const { data: commonParameters } = useFetchParameterPropertiesQuery({
    code: COMMON_PARAMETERS.STATE,
  })

  const [fetchObjectXByCode, { data: objectXObjectModel }] = useFetchObjectByCodeNoCacheMutation()
  const [fetchObjectYByCode, { data: objectYObjectModel }] = useFetchObjectByCodeNoCacheMutation()
  const [executeCommand] = useExecuteCommandMutation()

  const defaultDateIntervals = useMemo(() => {
    if (config.mode === TIME_LINE_MODE.OBJECT) {
      return []
    }

    return getDateIntervalsDefaultValue(config)
  }, [config])

  const [objectYSARSQLFilter, setObjectYSARSQLFilter] = useState<string | null>(null)
  const [objectXSARSQLFilter, setObjectXSARSQLFilter] = useState<string | null>(null)
  const [objectYSAFiltersCriteria, setObjectYSAFiltersCriteria] = useState<POSTObjectDataFilter>()
  const [objectXSAFiltersCriteria, setObjectXSAFiltersCriteria] = useState<POSTObjectDataFilter>()
  const [objectXColumnsObjectSARSQLFilter, setObjectXColumnsObjectSARSQLFilter] = useState<
    string | null
  >(null)
  const [objectXColumnsObjectSAFiltersCriteria, setObjectXColumnsObjectSAFiltersCriteria] =
    useState<POSTObjectDataFilter>()

  const [dateIntervals, setDateIntervals] = useState(defaultDateIntervals)
  const [timeLineGridData, setTimeLineGridData] = useState<TimeLineGridDataRecord[]>([])
  const timeLineGridDataRef = useRef<TimeLineGridDataRecord[]>([])
  timeLineGridDataRef.current = timeLineGridData
  const [isLoadingTimeLineGridData, setIsLoadingTimeLineGridData] = useState(true)
  const [isLoadingColumnsObjectData, setIsLoadingColumnsObjectData] = useState(
    config.mode === TIME_LINE_MODE.OBJECT
  )
  const [openedViewConfig, setOpenedViewConfig] = useState<{
    viewId?: string
    viewCode?: string
    gridCellParams?: GridCellParams
    defaultFilter?: string
  } | null>(null)
  const [columnsObjectData, setColumnsObjectData] = useState<ObjectDataRecord[]>([])
  const [isUseMainObjectXBySAFilter, setIsUseMainObjectXBySAFilter] = useState(false)
  const [isFilterColumnsObjectByObjectXFilter, setIsFilterColumnsObjectByObjectXFilter] =
    useState(false)

  const isNeedWaitFirstLoadWithSA = useRef(Boolean(searchAssistantConfig?.id))
  const objectYPKField = usePkField(objectYObjectModel?.fields) as ObjectFieldDTO
  const objectXPKField = usePkField(objectXObjectModel?.fields) as ObjectFieldDTO
  const cellContextRef = useRef<TimelineCellContext>({
    objectYTotalCount: 0,
    selectedCells: {},
    isSelectionDragging: false,
    hasSelectionDragged: false,
    dragStartCell: null,
    dynamicColumnIds: [],
    isDraggingActive: false,
  })
  const objectXDataRef = useRef<ObjectDataRecord[]>([])
  const dragItemsForGetTotals = useRef<TimelineCellMove[]>([])

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const { executeActions } = useExecuteActions()

  const isDateIntervalsChanged = useMemo(() => {
    return !isEqual(defaultDateIntervals, dateIntervals)
  }, [dateIntervals, defaultDateIntervals])

  const { startDate, endDate } = useMemo(() => {
    if (config.mode === TIME_LINE_MODE.OBJECT) {
      return {}
    }

    return {
      startDate: dateIntervals[0].startDate,
      endDate: dateIntervals[dateIntervals.length - 1].endDate,
    }
  }, [dateIntervals])

  const methods = useForm({
    defaultValues: {
      dateRange:
        config.mode === TIME_LINE_MODE.OBJECT ? [] : [startDate as string, endDate as string],
    },
  })

  const { watch } = methods

  const watchDateRange = watch('dateRange')

  const objectYActionsObject: Record<EVENT_CODE, ActionRow> = useMemo(() => {
    if (!config.objectY.actions?.length) {
      return {} as Record<EVENT_CODE, ActionRow>
    }

    return config.objectY.actions.reduce(
      (acc, action) => ({ ...acc, [action.eventCode as string]: action }),
      {}
    ) as Record<EVENT_CODE, ActionRow>
  }, [config.objectY.actions])
  const { actionsObject: objectXActionsObject } = useGetObjectXActions({
    configActions: config.objectX?.actions,
  })

  const { handlers: restrictionsHandlers, state: restrictionsState } = useTimeLineRestrictions({
    config,
    apiRef: apiRef as MutableRefObject<GridApiPro>,
    objectXInitialRestrictions,
    objectYInitialRestrictions,
  })

  const { handleClearSelectedCells, handleChangeSelectedCells, handleToggleSelectedEvent } =
    useCellSelection({ cellContextRef, apiRef, timeLineGridDataRef })

  useEffect(() => {
    fetchObjectXByCode(config.objectX.objectCode)
    fetchObjectYByCode(config.objectY.objectCode)
  }, [])

  const handleUpdateDateIntervals = (dateRange: string[]) => {
    const newDateIntervals = splitIntervalByPeriod(dateRange[0], dateRange[1], 'days')

    setDateIntervals(newDateIntervals)

    return newDateIntervals
  }

  const handleResetDateIntervals = () => {
    setDateIntervals(getDateIntervalsDefaultValue(config))
  }

  function handleOpenForm(
    formProps: OpenFormProps,
    event: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>,
    additionalProps?: {
      addHistory?: boolean
    }
  ) {
    const { id } = formProps

    if (checkMaxOpenedWindowsCount(initialCommonParameters)) {
      return
    }

    const globalId = uuid()

    const initialPosition = getInitialPositionWindow(event)

    if (additionalProps?.addHistory) {
      dispatch(
        addFormHistory({
          id: id || globalId,
          form: {
            ...formProps,
            isWindow: true,
            title: formProps.formCode,
            objectId: formProps.objectId || null,
            id: id || globalId,
          },
        })
      )
    }

    dispatch(
      openFormDialog({
        id: id || globalId,
        parentDialogId: null,
        type: DIALOG_WINDOW_TYPE.FORM,
        title: null,
        meta: {
          ...formProps,
          globalId: id || globalId,
        },
        initialPosition,
      })
    )
  }

  const handleChangeTimeLineGridData = (
    data: TimeLineGridDataRecord[],
    isRefresh?: boolean,
    objectXData?: ObjectDataRecord[]
  ) => {
    if (isRefresh) {
      setTimeLineGridData(
        data.map(item => {
          return {
            ...item,
            startDate,
            endDate,
          }
        })
      )

      objectXDataRef.current = objectXData || []

      return
    }

    objectXDataRef.current = [...objectXDataRef.current, objectXData || []]
    setTimeLineGridData(prevState => [
      ...prevState,
      ...data.map(item => {
        return {
          ...item,
          startDate,
          endDate,
        }
      }),
    ])
  }

  const handleDeleteTimeLineObjectXEventById = (objectId: string) => {
    setTimeLineGridData(prevTimelineGridData => {
      return prevTimelineGridData.map(item => {
        const row = { ...item }

        for (const key in row) {
          if (isArray(row[key as keyof DateIntervalKey])) {
            const filteredItem = row[key as keyof DateIntervalKey]?.find(
              (item: ObjectDataRecord) => item[objectXPKField?.name as string] === objectId
            )

            const filteredEvents = row[key as keyof DateIntervalKey]?.filter(
              (item: ObjectDataRecord) => item[objectXPKField?.name as string] !== objectId
            )

            const cellId = getCellId(row.id, key)
            const isEventExistInSelectedCells = cellContextRef.current.selectedCells[
              cellId
            ]?.events?.find(item => item[objectXPKField?.name] === objectId)

            if (filteredItem && isEventExistInSelectedCells) {
              const dates = key.slice(1, -1).split(';')

              handleToggleSelectedEvent({
                cellId,
                event: filteredItem,
                pkFieldName: objectXPKField?.name,
                columnId: key,
                rowId: row.id,
                endDate: dates[1],
                startDate: dates[0],
                rowData: row[key as keyof DateIntervalKey] as ObjectDataRecord[],
              })
            }

            row[key as keyof DateIntervalKey] = filteredEvents
          }
        }

        return row
      })
    })
  }

  const handleAddTimeLineObjectXEvent = (data: {
    itemIndex: number
    dateIntervalKey: keyof DateIntervalKey
    key: string
    newItem: ObjectDataRecord
    objectId: string
  }) => {
    setTimeLineGridData(prevTimelineGridData => {
      const { itemIndex, newItem, key } = data

      const updatedPrevTimelineGridData = structuredClone(prevTimelineGridData)

      const cellId = getCellId(updatedPrevTimelineGridData[itemIndex].id, key)

      if (
        Boolean(cellContextRef.current.selectedCells[cellId]) &&
        !cellContextRef.current.selectedCells[cellId].events?.length
      ) {
        const dates = key?.slice(1, -1)?.split(';')

        handleChangeSelectedCells({
          cellId,
          columnId: key,
          objectXItems: [],
          rowId: updatedPrevTimelineGridData[itemIndex].id,
          endDate: dates[1],
          startDate: dates[0],
          rowData: updatedPrevTimelineGridData[itemIndex],
        })
      }

      updatedPrevTimelineGridData[itemIndex][key]?.push({
        ...newItem,
        columnInfo: updatedPrevTimelineGridData?.[itemIndex]?.columnsInfo?.[key],
      })

      return updatedPrevTimelineGridData
    })
  }

  const handleUpdateTimeLineObjectXEvent = (data: {
    itemIndex: number
    key: string
    newItem: ObjectDataRecord
    objectId: string
  }) => {
    setTimeLineGridData(prevTimelineGridData => {
      const { itemIndex, newItem, key, objectId } = data

      const updatedPrevTimelineGridData = structuredClone(prevTimelineGridData)

      updatedPrevTimelineGridData[itemIndex][key] = updatedPrevTimelineGridData[itemIndex][
        key
      ]?.map((item: ObjectDataRecord) => {
        if (item[objectXPKField?.name as string] === objectId) {
          return {
            ...newItem,
            columnInfo: updatedPrevTimelineGridData?.[itemIndex]?.columnsInfo?.[key],
          }
        }

        return item
      })

      return updatedPrevTimelineGridData
    })
  }

  const handleChangeIsNeedWaitFirstLoadWithSA = (value: boolean) => {
    isNeedWaitFirstLoadWithSA.current = value
  }

  const handleChangeObjectYSARSQLFilter = (filter: string | null) => {
    setObjectYSARSQLFilter(filter)
  }

  const handleChangeObjectXSARSQLFilter = (filter: string | null) => {
    setObjectXSARSQLFilter(filter)
  }

  const handleChangeIsLoadingTimeLineGridData = (isLoading: boolean) => {
    setIsLoadingTimeLineGridData(isLoading)
  }

  const handleChangeVariables = (variables: Record<string, GETVariableDTO>) => {
    dispatch(setTimelineVariables({ variables }))
  }

  const handleExecuteActions = (data: {
    actions: ActionRow[]
    srcObj: ObjectDataRecord
    eventCode: EVENT_CODE
  }) => {
    const { actions, eventCode, srcObj } = data

    const openViewAction = actions.find(action => {
      return action.actionCode === ACTION_CODE.OPEN_VIEW
    })

    if (openViewAction?.eventCode === eventCode && !openedViewConfig) {
      let defaultFilter = ''

      if (openViewAction.jsValue) {
        defaultFilter = executeCode(openViewAction.jsValue, {
          srcObj: {
            ...srcObj,
            startDate: watchDateRange[0],
            endDate: watchDateRange[1],
          },
        }) as string
      }

      setTimeout(() => {
        handleOpenView({
          action: openViewAction,
          gridCellParams: srcObj.gridCellParams,
          defaultFilter,
        })
      }, 250)
    }

    const filteredActions = openViewAction
      ? actions.filter(action => action.id !== openViewAction.id)
      : actions

    return executeActions({
      ...data,
      actions: filteredActions,
    })
  }

  const handleMoveCellsBetweenPositions = (moves: TimelineCellMove[]): boolean => {
    if (!moves.length || !objectXPKField?.name) {
      return false
    }

    const pkName = objectXPKField.name
    const touchedRowIds = new Set(moves.flatMap(m => [m.sourceRowId, m.targetRowId]))

    handleChangeDragItemsForGetTotalsWithMoves(moves)

    setTimeLineGridData(prev => {
      const next = applyTimelineCellMovesToRows(prev, moves, pkName)

      moves
        .filter(move => !move.source?.isSelected)
        .forEach(move => {
          const targetCellId = getCellId(move.targetRowId, move.targetColumnId)

          if (
            Boolean(cellContextRef.current.selectedCells[targetCellId]) &&
            !cellContextRef.current.selectedCells[targetCellId].events?.length
          ) {
            const dates = move.targetColumnId?.slice(1, -1)?.split(';')

            handleChangeSelectedCells({
              cellId: targetCellId,
              columnId: move.targetColumnId,
              objectXItems: [],
              rowId: move.targetRowId,
              endDate: dates[1],
              startDate: dates[0],
              rowData: move.target?.rowData || {},
            })
          }
        })

      const api = apiRef?.current

      if (api && typeof api.updateRows === 'function') {
        api.updateRows(next.filter(row => touchedRowIds.has(row.id)))
      }

      return next
    })

    return true
  }

  const { cellItemDrag } = useCellItemDragAndDrop({
    cellContextRef,
    timeLineGridData,
    config,
    objectXPKField,
    handleMoveCellsBetweenPositions,
    handleExecuteActions,
    handleClearSelectedCells,
    executeCommand,
    dispatch,
  })

  const handleChangeObjectYSAFiltersCriteria = (criteria: POSTObjectDataFilter) => {
    setObjectYSAFiltersCriteria(criteria)
  }

  const handleChangeObjectXSAFiltersCriteria = (criteria: POSTObjectDataFilter) => {
    setObjectXSAFiltersCriteria(criteria)
  }

  const handleChangeObjectXColumnsObjectSARSQLFilter = (filter: string | null) => {
    setObjectXColumnsObjectSARSQLFilter(filter)
  }

  const handleChangeObjectXColumnsObjectSAFiltersCriteria = (criteria: POSTObjectDataFilter) => {
    setObjectXColumnsObjectSAFiltersCriteria(criteria)
  }

  const handleOpenView = (data: {
    action: ActionRow
    gridCellParams?: GridCellParams
    defaultFilter?: string
  }) => {
    const { action, gridCellParams, defaultFilter } = data

    setOpenedViewConfig({
      viewId: action.objectViewId,
      viewCode: action.objectViewCode,
      gridCellParams,
      defaultFilter,
    })
  }

  const handleCloseView = () => {
    setOpenedViewConfig(null)
  }

  const handleChangeColumnsObjectData = (objectData: ObjectDataRecord[]) => {
    setColumnsObjectData(objectData)
  }

  const handleChangeIsLoadingColumnsObjectData = (isLoading: boolean) => {
    setIsLoadingColumnsObjectData(isLoading)
  }

  const handleSetIsUseMainObjectXBySAFilter = (value: boolean) => {
    setIsUseMainObjectXBySAFilter(value)
  }

  const handleChangeIsFilterColumnsObjectByObjectXFilter = (value: boolean) => {
    setIsFilterColumnsObjectByObjectXFilter(value)
  }

  const handleChangeDragItemsForGetTotalsWithMoves = (moves: TimelineCellMove[]) => {
    // Сохраняем перемещения у которых была изменена колонка для запроса тоталов по колонке source при получении данных по сокетам

    if (config.objectX.totalsRows?.length) {
      moves.forEach(move => {
        if (move.sourceColumnId !== move.targetColumnId) {
          dragItemsForGetTotals.current.push(move)
        }
      })
    }
  }

  const handleDeleteDragItemForGetTotals = (sourceColumnId: string) => {
    dragItemsForGetTotals.current = dragItemsForGetTotals.current.filter(
      item => item.sourceColumnId !== sourceColumnId
    )
  }

  return {
    state: {
      methods,
      dateIntervals,
      timeLineGridData,
      isLoadingTimeLineGridData,
      isLoadingColumnsObjectData,
      objectYSAFiltersCriteria,
      objectXSAFiltersCriteria,
      watchDateRange,
      openedViewConfig,
      objectXSARSQLFilter,
      objectYSARSQLFilter,
      objectXColumnsObjectSARSQLFilter,
      objectXColumnsObjectSAFiltersCriteria,
      columnsObjectData,
      isUseMainObjectXBySAFilter,
      isFilterColumnsObjectByObjectXFilter,
      ...restrictionsState,
    },
    data: {
      startDate,
      endDate,
      objectYActionsObject,
      objectXActionsObject,
      objectXObjectModel,
      objectYObjectModel,
      objectYPKField,
      objectXPKField,
      isNeedWaitFirstLoadWithSA: Boolean(isNeedWaitFirstLoadWithSA.current),
      apiRef,
      isDateIntervalsChanged,
      cellContextRef,
      objectXDataRef,
      dragItemsForGetTotals,
    },
    handlers: {
      handleUpdateDateIntervals,
      handleOpenForm,
      handleChangeTimeLineGridData,
      handleChangeIsLoadingTimeLineGridData,
      handleChangeIsNeedWaitFirstLoadWithSA,
      handleChangeVariables,
      handleExecuteActions,
      handleResetDateIntervals,
      handleChangeObjectYSAFiltersCriteria,
      handleOpenView,
      handleCloseView,
      handleClearSelectedCells,
      handleChangeSelectedCells,
      handleToggleSelectedEvent,
      handleDeleteTimeLineObjectXEventById,
      handleAddTimeLineObjectXEvent,
      handleUpdateTimeLineObjectXEvent,
      handleChangeObjectYSARSQLFilter,
      handleChangeObjectXSARSQLFilter,
      handleChangeObjectXSAFiltersCriteria,
      handleChangeObjectXColumnsObjectSARSQLFilter,
      handleChangeObjectXColumnsObjectSAFiltersCriteria,
      handleChangeColumnsObjectData,
      handleChangeIsLoadingColumnsObjectData,
      handleMoveCellsBetweenPositions,
      handleSetIsUseMainObjectXBySAFilter,
      handleChangeIsFilterColumnsObjectByObjectXFilter,
      handleDeleteDragItemForGetTotals,
      cellItemDrag,
      ...restrictionsHandlers,
    },
  }
}
